// "use client"

import React, { useState, useEffect, useMemo } from "react";
import { getClientes, createCliente, updateCliente } from "../../services/clientesService";
import "./Clientes.css";
import {
  Phone,
  Edit,
  Plus,
  MapPin,
  Building2,
  IdCard,
  Car,
  X,
  CheckCircle2,
  XCircle,
  Search,
  User,
  Shield
} from "lucide-react";

const getSucursal = () => localStorage.getItem("idsucursal");

/* -------------------- Helpers de validación -------------------- */
const REGEX_SOLO_LETRAS = /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+$/;
const REGEX_PLACA = /^[A-Z]{3}\d{3,4}$/;
const REGEX_CELULAR_10 = /^\d{10}$/;

/** Valida "Nombre Apellido" (dos palabras mínimas) con 2..12 letras cada una */
function validarNombreCompleto(input) {
  if (!input) return { ok: false, msg: "El nombre completo es obligatorio." };
  const parts = input.trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) {
    return { ok: false, msg: "Debe ingresar nombre y apellido (mínimo 2 palabras)." };
  }
  const [nombre, apellido] = parts;

  const check = (w) =>
    REGEX_SOLO_LETRAS.test(w) && w.length >= 2 && w.length <= 12;

  if (!check(nombre)) {
    return { ok: false, msg: "El nombre debe tener 2 a 12 letras (sin números)." };
  }
  if (!check(apellido)) {
    return { ok: false, msg: "El apellido debe tener 2 a 12 letras (sin números)." };
  }
  return { ok: true };
}

/**
 * Normaliza una placa: solo letras y dígitos en mayúsculas, SIN guion.
 * El guion (u otro separador) que escriba o pegue el usuario se descarta,
 * de modo que las placas siempre se guardan en formato AAA0000.
 */
function sanitizarPlaca(v) {
  let s = (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  // Limitar longitud máxima a AAA0000 (7)
  if (s.length > 7) s = s.slice(0, 7);
  return s;
}

/* ======================= Modal Crear/Editar ======================= */
const ClienteModal = ({ cliente, onClose, onSave, existingClientes }) => {
  const [formData, setFormData] = useState({
    nombre: "",
    telefono: "",
    placa: "",
    cedula: "",
    empresa: "",
    direccion: "",
    estado: true,
  });
  const [error, setError] = useState("");
  const isEditMode = cliente !== null;

  useEffect(() => {
    if (isEditMode) {
      setFormData({
        id: cliente.id,
        nombre: cliente.nombre || "",
        telefono: cliente.telefono || "",
        placa: sanitizarPlaca(cliente.placa || ""),
        cedula: cliente.cedula || "",
        empresa: cliente.empresa || "",
        direccion: cliente.direccion || "",
        estado: cliente.estado !== null ? cliente.estado : true,
      });
    }
  }, [cliente, isEditMode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "telefono") {
      // Solo dígitos y máximo 10
      const soloDigitos = value.replace(/\D/g, "").slice(0, 10);
      setFormData((p) => ({ ...p, telefono: soloDigitos }));
      return;
    }
    if (name === "placa") {
      setFormData((p) => ({ ...p, placa: sanitizarPlaca(value) }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const idSucu = getSucursal();
    if (!idSucu) {
      setError("No se ha seleccionado una sucursal.");
      return;
    }

    // --- Validaciones solicitadas ---
    // 1) Nombre completo (nombre y apellido, 2..12 letras cada uno)
    const vNombre = validarNombreCompleto(formData.nombre);
    if (!vNombre.ok) {
      setError(vNombre.msg);
      return;
    }

    // 2) Celular: exactamente 10 dígitos
    if (!REGEX_CELULAR_10.test(formData.telefono)) {
      setError("El número de celular debe tener exactamente 10 dígitos (solo números).");
      return;
    }

    // 3) Placa: AAA000 o AAA0000 (sin guion)
    const placaFmt = sanitizarPlaca(formData.placa);
    if (!REGEX_PLACA.test(placaFmt)) {
      setError("La placa no es válida. Formatos permitidos: AAA000 o AAA0000 (sin guion).");
      return;
    }

    // Duplicado por placa. Los clientes antiguos pueden estar guardados con
    // guion, así que se normalizan ambos lados antes de comparar.
    const isDuplicate = !isEditMode && existingClientes.some(
      (c) => sanitizarPlaca(c.placa) === placaFmt
    );
    if (isDuplicate) {
      setError("Ya existe un cliente con esta placa. No se puede crear un duplicado.");
      return;
    }

    const payload = {
      nombre: formData.nombre.trim(),
      telefono: formData.telefono,
      placa: placaFmt,
      cedula: formData.cedula || null,
      empresa: formData.empresa || null,
      direccion: formData.direccion || null,
      sucursalId: idSucu,
      estado: formData.estado,
    };

    try {
      if (isEditMode) {
        await updateCliente(cliente.id, payload);
      } else {
        await createCliente(payload);
      }
      onSave();
      onClose();
    } catch (err) {
      setError(err.message || "Ocurrió un error al guardar.");
    }
  };

  return (
    <div className="modal-overlay cliente-modal-scope" onClick={onClose}>
      <div className="cliente-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="cliente-modal-header">
          <h3>{isEditMode ? "Editar Cliente" : "Crear Nuevo Cliente"}</h3>
          <button onClick={onClose} className="cliente-modal-close-btn">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="cliente-modal-content">
          <div className="cliente-form-section-title">Datos Principales (Obligatorios)</div>

          <div className="cliente-form-grid">
            <div className="cliente-form-group">
              <label>Nombre Completo</label>
              <input
                type="text"
                name="nombre"
                placeholder="Ej: Juan Vaca"
                value={formData.nombre}
                onChange={handleChange}
                required
              />
            </div>

            <div className="cliente-form-group">
              <label>Teléfono (celular 10 dígitos)</label>
              <input
                type="tel"
                name="telefono"
                inputMode="numeric"
                pattern="\d*"
                maxLength={10}
                placeholder="0991234567"
                value={formData.telefono}
                onChange={handleChange}
                required
              />
            </div>

            <div className="cliente-form-group">
              <label>Placa</label>
              <input
                type="text"
                name="placa"
                placeholder="AAA0000"
                value={formData.placa}
                onChange={handleChange}
                maxLength={7} /* AAA0000 */
                required
              />
            </div>
          </div>

          <div className="cliente-form-section-title">Datos Adicionales (Opcionales)</div>
          <div className="cliente-form-grid">
            <div className="cliente-form-group">
              <label>Cédula</label>
              <input type="text" name="cedula" value={formData.cedula} onChange={handleChange} />
            </div>
            <div className="cliente-form-group">
              <label>Empresa</label>
              <input type="text" name="empresa" value={formData.empresa} onChange={handleChange} />
            </div>
            <div className="cliente-form-group">
              <label>Dirección</label>
              <input type="text" name="direccion" value={formData.direccion} onChange={handleChange} />
            </div>
          </div>

          <div className="cliente-form-group-checkbox">
            <label>Estado del Cliente</label>
            <label className="switch">
              <input type="checkbox" name="estado" checked={formData.estado} onChange={handleChange} />
              <span className="slider round"></span>
            </label>
          </div>

          {/* Mensaje de validación/alerta al final del modal */}
          {error && <p className="cliente-form-error">{error}</p>}

          <div className="cliente-form-actions">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              <Plus size={16} /> {isEditMode ? "Guardar Cambios" : "Crear Cliente"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ======================= Tarjeta de cliente ======================= */
const ClienteIDCard = ({ cliente, onEdit }) => (
  <div className="clientes-card">
    <div className="clientes-card-content">
      <div className="clientes-card-header">
        <h3 className="clientes-card-name">{cliente.nombre}</h3>
        <div className="clientes-card-header-actions">
          <span className={`cliente-status-badge ${cliente.estado ? "activo" : "inactivo"}`}>
            {cliente.estado ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
            {cliente.estado ? "Activo" : "Inactivo"}
          </span>
          <button onClick={() => onEdit(cliente)} className="clientes-edit-btn" title="Editar Cliente">
            <Edit size={16} />
          </button>
        </div>
      </div>
      <div className="clientes-card-details">
        <div className="cliente-detail-item placa">
          <Car size={14} className="cliente-detail-icon" />
          <span>{cliente.placa}</span>
        </div>
        <div className="cliente-detail-item telefono">
          <Phone size={14} className="cliente-detail-icon" />
          <span>{cliente.telefono}</span>
        </div>
        {cliente.cedula && (
          <div className="cliente-detail-item cedula">
            <IdCard size={14} className="cliente-detail-icon" />
            <span>{cliente.cedula}</span>
          </div>
        )}
        {cliente.empresa && (
          <div className="cliente-detail-item empresa">
            <Building2 size={14} className="cliente-detail-icon" />
            <span>{cliente.empresa}</span>
          </div>
        )}
        {cliente.direccion && (
          <div className="cliente-detail-item direccion full-width">
            <MapPin size={14} className="cliente-detail-icon" />
            <span>{cliente.direccion}</span>
          </div>
        )}
      </div>
    </div>
  </div>
);

/* ======================= Página de clientes ======================= */
const Clientes = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("activos");

  const fetchClientes = async () => {
    try {
      setLoading(true);
      const data = await getClientes();
      setClientes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientes();
  }, []);

  const filteredClientes = useMemo(() => {
    return clientes
      .filter((cliente) => {
        if (statusFilter === "activos") return cliente.estado === true;
        if (statusFilter === "inactivos") return cliente.estado === false;
        return true;
      })
      .filter((cliente) => {
        const term = searchTerm.toLowerCase();
        return (
          cliente.nombre.toLowerCase().includes(term) ||
          (cliente.placa && cliente.placa.toLowerCase().includes(term)) ||
          (cliente.cedula && cliente.cedula.toLowerCase().includes(term))
        );
      });
  }, [clientes, searchTerm, statusFilter]);

  const handleEdit = (cliente) => {
    setEditingClient(cliente);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingClient(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
  };

  const handleSave = () => {
    fetchClientes();
  };

  return (
    <>
      <div className="clientes-page-container">
        <div className="page-header">
          <div className="title-container">
            <h1 className="title-main">Gestión de Clientes</h1>
          </div>
          <div className="header-decoration"></div>
        </div>

        <div className="clientes-filter-bar">
          <div className="clientes-search-container">
            <Search className="clientes-search-icon" size={20} />
            <input
              type="text"
              placeholder="Buscar por nombre, placa o cédula..."
              className="clientes-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="clientes-status-filter-container">
            <button
              className={`clientes-filter-btn ${statusFilter === "todos" ? "active" : ""}`}
              onClick={() => setStatusFilter("todos")}
            >
              Todos
            </button>
            <button
              className={`clientes-filter-btn ${statusFilter === "activos" ? "active" : ""}`}
              onClick={() => setStatusFilter("activos")}
            >
              Activos
            </button>
            <button
              className={`clientes-filter-btn ${statusFilter === "inactivos" ? "active" : ""}`}
              onClick={() => setStatusFilter("inactivos")}
            >
              Inactivos
            </button>
          </div>
        </div>

        {loading && <div className="message-container">Cargando clientes...</div>}
        {error && <div className="message-container error">Error: {error}</div>}

        {!loading && !error && (
          <div className="clientes-grid-container">
            {filteredClientes.length > 0 ? (
              filteredClientes.map((cliente) => (
                <ClienteIDCard key={cliente.id} cliente={cliente} onEdit={handleEdit} />
              ))
            ) : (
              <div className="message-container">No se encontraron clientes con los filtros aplicados.</div>
            )}
          </div>
        )}
      </div>

      <button onClick={handleCreate} className="clientes-add-fab-btn" title="Añadir Nuevo Cliente">
        <Plus size={28} />
      </button>

      {isModalOpen && (
        <ClienteModal
          cliente={editingClient}
          onClose={handleCloseModal}
          onSave={handleSave}
          existingClientes={clientes}
        />
      )}
    </>
  );
};

export default Clientes;
